import { formatDateScalarString } from '@/common/dates';
import { LogoutButton } from '@/components/buttons';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { useCurrentUser } from '@/hooks';
import { PrimaryLayout } from '@/layouts/primary';
import { TopNavFor } from '@/layouts/primary/top-nav';
import { useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import { UserAlertDay, UserAlertFrequency } from '../../../../backend/src/types.generated';
import { BinReminderForm, BinReminderFormValues } from './form/component';
import {
	useGetUserAccountAlertsQuery,
	useUpdateUserAccountAlertsNestedMutation,
} from './graphql.generated';

export const AlertsPage = () => {
	const navigate = useNavigate();
	const { currentUser } = useCurrentUser();
	const { data } = useGetUserAccountAlertsQuery({
		variables: {
			id: currentUser?.id ?? '',
		},
		skip: !currentUser?.id,
	});

	const initialValues = useMemo(() => {
		const fullTime = data?.user?.userAlerts?.[0]?.time;
		const splitTime = fullTime ? fullTime.split(':') : undefined;
		const formattedTime = splitTime ? `${splitTime[0]}:${splitTime[1]}` : '';
		const incomingFrom = data?.user?.userAlerts?.[0]?.from;
		const formattedFrom = incomingFrom ? new Date(incomingFrom) : null;
		return data?.user
			? {
					binReminderEnabled: data?.user?.binReminderEnabled ?? false,
					frequency: data?.user?.userAlerts?.[0]?.frequency ?? UserAlertFrequency.Weekly,
					day: data?.user?.userAlerts?.[0]?.day ?? UserAlertDay.Monday,
					time: formattedTime,
					from: formattedFrom,
					id: data?.user?.userAlerts?.[0]?.id,
				}
			: undefined;
	}, [data]);

	const [updateUserAccountAlertsNested, { loading: updatingUser }] =
		useUpdateUserAccountAlertsNestedMutation();

	const formRef = useRef<FormikProps<BinReminderFormValues>>(null);
	const triggerSubmit = useCallback(() => {
		formRef.current?.submitForm();
	}, [formRef]);

	const saveAlertButton = useMemo(
		() => (
			<StandardButton loading={updatingUser} onClick={triggerSubmit}>
				Save Alert
			</StandardButton>
		),
		[triggerSubmit, updatingUser]
	);

	const logoutButton = useMemo(() => <LogoutButton />, []);

	const handleSubmit = useCallback(
		async (values: BinReminderFormValues) => {
			const [hours, minutes] = values.time.split(':');
			const time = `${hours}:${minutes}:00`;
			await updateUserAccountAlertsNested({
				variables: {
					alertInput: {
						day: values.day,
						time,
						frequency: values.frequency,
						from: formatDateScalarString(values.from),
						id: values.id,
						user: {
							id: currentUser?.id ?? '',
						},
					},
					userInput: {
						id: currentUser?.id ?? '',
						binReminderEnabled: values.binReminderEnabled,
					},
				},
				refetchQueries: ['GetUserAccountAlerts'],
			});

			navigate({ to: '/home' });
		},
		[currentUser?.id, navigate, updateUserAccountAlertsNested]
	);

	return (
		<PrimaryLayout
			header={{ title: 'Account', backTo: '/home', children: logoutButton }}
			topNav={TopNavFor.ACCOUNT}
			footer={{ children: saveAlertButton }}
		>
			<ContentPanel gap="md">
				{initialValues && (
					<BinReminderForm
						formRef={formRef}
						onSubmit={handleSubmit}
						initialValues={initialValues}
					/>
				)}
			</ContentPanel>
		</PrimaryLayout>
	);
};
